// DisciplinasView — CRUD + offerings + professores

import { signal } from "@preact/signals";
import { useEffect, useMemo, useState } from "preact/hooks";
import {
  AlertCircle,
  BookOpen,
  CheckCircle2,
  Hourglass,
  Loader2,
  Pencil,
  Plus,
  Save,
  Search,
  X
} from "lucide-react";
import { Switch } from "@app/ui/components/switch";
import { api, ApiError, type DisciplinaDetail, type DisciplinaRow, type TurmaRow } from "../api";
import { openAlert } from "../ConfirmPromptModal";
import { friendlyError } from "@app/ui/lib/friendly-error";

const disciplinas = signal<DisciplinaRow[] | null>(null);
const turmas = signal<TurmaRow[]>([]);
const phase = signal<"loading" | "ready" | "error">("loading");
const errMsg = signal<string | null>(null);
const search = signal<string>("");
const editing = signal<DisciplinaDetail | null>(null);
const creating = signal<boolean>(false);

async function load() {
  phase.value = "loading";
  errMsg.value = null;
  try {
    const [d, t] = await Promise.all([api.listDisciplinas(), api.listTurmas()]);
    disciplinas.value = d.disciplinas;
    turmas.value = t.turmas;
    phase.value = "ready";
  } catch (e) {
    if (e instanceof ApiError && (e.status === 401 || e.status === 403)) {
      window.location.href = "/auth/";
      return;
    }
    errMsg.value = friendlyError(e);
    phase.value = "error";
  }
}

async function openEdit(id: string) {
  try {
    const r = await api.getDisciplina(id);
    editing.value = r.disciplina;
  } catch (e) {
    void openAlert({ title: "Erro", message: friendlyError(e) });
  }
}

export function DisciplinasView() {
  useEffect(() => {
    load();
  }, []);
  if (phase.value === "loading")
    return (
      <p class="flex items-center gap-2 text-muted-foreground">
        <Loader2 className="size-4 animate-spin" /> Carregando…
      </p>
    );
  if (phase.value === "error")
    return (
      <div class="rounded-md border border-destructive/30 bg-destructive/5 px-4 py-3 text-sm">
        <AlertCircle className="mr-2 inline size-4 text-destructive" /> {errMsg.value}
      </div>
    );
  const rows = disciplinas.value ?? [];
  const q = search.value.toLowerCase().trim();
  const visible = useMemo(() => {
    return rows.filter((d) => {
      if (q) {
        const hay = `${d.name} ${d.code ?? ""} ${d.description ?? ""}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [rows, q]);

  return (
    <div class="space-y-4">
      <header class="flex items-start justify-between gap-3">
        <div>
          <h1 class="text-2xl font-bold tracking-tight">Disciplinas</h1>
          <p class="mt-1 text-sm text-muted-foreground">
            Configure as disciplinas, associe professores e turmas.
          </p>
        </div>
        <button
          type="button"
          onClick={() => (creating.value = true)}
          class="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-xs font-bold text-white hover:brightness-110"
        >
          <Plus width="1.1em" height="1.1em" /> Nova disciplina
        </button>
      </header>

      <div class="flex flex-wrap items-center gap-2 rounded-md border border-border bg-card/40 px-3 py-2">
        <div class="relative min-w-[240px] flex-1">
          <Search
            width="1em"
            height="1em"
            className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground"
          />
          <input
            type="search"
            value={search.value}
            onInput={(e) => (search.value = (e.target as HTMLInputElement).value)}
            placeholder="Buscar nome, código…"
            class="w-full rounded-md border border-border bg-background pl-8 pr-3 py-1.5 text-sm outline-none focus:border-primary"
          />
        </div>
      </div>

      {visible.length === 0 ? (
        <div class="rounded-md border border-dashed border-border bg-card/30 px-4 py-8 text-center text-sm text-muted-foreground">
          Nenhuma disciplina ainda.
        </div>
      ) : (
        <div class="overflow-hidden rounded-md border border-border bg-card/40">
          <table class="w-full text-sm">
            <thead>
              <tr class="border-b border-border bg-card/60 text-left text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
                <th class="px-3 py-2">Disciplina</th>
                <th class="px-3 py-2">Status</th>
                <th class="px-3 py-2 text-right">Professores</th>
                <th class="px-3 py-2 text-right">Turmas</th>
                <th class="px-3 py-2 text-right">Ações</th>
              </tr>
            </thead>
            <tbody>
              {visible.map((d) => (
                <tr key={d.id} class="border-b border-border/60 last:border-0 hover:bg-accent/30">
                  <td class="px-3 py-2">
                    <div class="flex items-center gap-1.5 font-semibold">
                      <BookOpen width="0.9em" height="0.9em" className="text-violet-600 dark:text-violet-300" />
                      {d.name}
                    </div>
                    <div class="font-mono text-[11px] text-muted-foreground">
                      {d.code ?? "—"}
                      {d.gradingEnabled && " · com nota"}
                    </div>
                  </td>
                  <td class="px-3 py-2">
                    <StatusBadge status={d.status} />
                  </td>
                  <td class="px-3 py-2 text-right tabular-nums">{d.teacherCount}</td>
                  <td class="px-3 py-2 text-right tabular-nums">{d.offeringCount}</td>
                  <td class="px-3 py-2 text-right">
                    <button
                      type="button"
                      onClick={() => openEdit(d.id)}
                      class="rounded-md border border-border bg-background px-2 py-1 text-xs hover:bg-accent"
                      title="Editar"
                      aria-label="Editar"
                    >
                      <Pencil width="0.9em" height="0.9em" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editing.value && <EditModal disciplina={editing.value} />}
      {creating.value && <CreateModal />}
    </div>
  );
}

function StatusBadge({ status }: { status: "draft" | "active" | "closed" }) {
  if (status === "active") {
    return (
      <span class="inline-flex items-center gap-1 rounded bg-emerald-500/15 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-emerald-700 dark:text-emerald-300">
        <CheckCircle2 width="0.85em" height="0.85em" /> Ativa
      </span>
    );
  }
  if (status === "closed") {
    return (
      <span class="inline-flex items-center gap-1 rounded bg-muted px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
        Encerrada
      </span>
    );
  }
  return (
    <span class="inline-flex items-center gap-1 rounded bg-amber-500/15 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-amber-700 dark:text-amber-300">
      <Hourglass width="0.85em" height="0.85em" /> Rascunho
    </span>
  );
}

function CreateModal() {
  const [name, setName] = useState("");
  const [code, setCode] = useState("");
  const [description, setDescription] = useState("");
  const [gradingEnabled, setGrading] = useState(true);
  const [centroCusto, setCentroCusto] = useState("");
  const [status, setStatus] = useState<"draft" | "active">("draft");
  const [busy, setBusy] = useState(false);

  async function go() {
    if (!name.trim()) return;
    setBusy(true);
    try {
      await api.createDisciplina({
        name: name.trim(),
        code: code.trim() || null,
        description: description.trim() || null,
        gradingEnabled,
        centroCusto: centroCusto.trim() || null,
        status
      });
      creating.value = false;
      await load();
    } catch (e) {
      void openAlert({ title: "Erro", message: friendlyError(e) });
    } finally {
      setBusy(false);
    }
  }
  return (
    <Modal title="Nova disciplina" onClose={() => (creating.value = false)}>
      <Field label="Nome *">
        <input
          type="text"
          value={name}
          onInput={(e) => setName((e.target as HTMLInputElement).value)}
          autoFocus
          aria-label="Nome"
          class="w-full rounded-md border border-border bg-background px-3 py-1.5 text-sm outline-none focus:border-primary"
        />
      </Field>
      <Field label="Código">
        <input
          type="text"
          value={code}
          onInput={(e) => setCode((e.target as HTMLInputElement).value)}
          aria-label="Código"
          placeholder="Ex: MED-101"
          class="w-full rounded-md border border-border bg-background px-3 py-1.5 text-sm outline-none focus:border-primary"
        />
      </Field>
      <Field label="Centro de custo">
        <input
          type="text"
          value={centroCusto}
          onInput={(e) => setCentroCusto((e.target as HTMLInputElement).value)}
          aria-label="Centro de custo"
          placeholder="Opcional (ex: Idiomas, Reforço escolar)"
          class="w-full rounded-md border border-border bg-background px-3 py-1.5 text-sm outline-none focus:border-primary"
        />
      </Field>
      <Field label="Descrição">
        <textarea
          value={description}
          onInput={(e) => setDescription((e.target as HTMLTextAreaElement).value)}
          rows={2}
          aria-label="Descrição"
          placeholder="Opcional"
          class="w-full resize-y rounded-md border border-border bg-background px-3 py-1.5 text-sm outline-none focus:border-primary"
        />
      </Field>
      <Field label="Tem nota?">
        <label class="flex items-center gap-2 text-xs">
          <Switch
            checked={gradingEnabled}
            onCheckedChange={(v) => setGrading(v)}
          />
          Esta disciplina computa nota dos alunos
        </label>
      </Field>
      <Field label="Status inicial">
        <div class="flex gap-2">
          {(["draft", "active"] as const).map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => setStatus(s)}
              class={`flex-1 rounded-md border px-3 py-1.5 text-xs font-semibold ${
                status === s
                  ? "border-primary/50 bg-primary/10 text-primary"
                  : "border-border bg-background text-muted-foreground hover:bg-accent"
              }`}
            >
              {s === "draft" ? "Rascunho" : "Ativa já"}
            </button>
          ))}
        </div>
      </Field>
      <ModalFooter>
        <button
          type="button"
          onClick={() => (creating.value = false)}
          disabled={busy}
          class="rounded-md border border-border bg-background px-3 py-1.5 text-xs font-semibold hover:bg-accent disabled:opacity-50"
        >
          Cancelar
        </button>
        <button
          type="button"
          onClick={go}
          disabled={busy || !name.trim()}
          class="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-xs font-bold text-white hover:brightness-110 disabled:opacity-50"
        >
          {busy ? <Loader2 className="size-3.5 animate-spin" /> : <Plus width="1em" height="1em" />}
          Criar
        </button>
      </ModalFooter>
    </Modal>
  );
}

function EditModal({ disciplina }: { disciplina: DisciplinaDetail }) {
  const [detail, setDetail] = useState<DisciplinaDetail>(disciplina);
  const [name, setName] = useState(disciplina.name);
  const [code, setCode] = useState(disciplina.code ?? "");
  const [description, setDescription] = useState(disciplina.description ?? "");
  const [gradingEnabled, setGrading] = useState(disciplina.gradingEnabled);
  const [centroCusto, setCentroCusto] = useState(disciplina.centroCusto ?? "");
  const [status, setStatus] = useState(disciplina.status);
  const [turmaPick, setTurmaPick] = useState("");
  const [busy, setBusy] = useState(false);

  async function save() {
    setBusy(true);
    try {
      const r = await api.patchDisciplina(disciplina.id, {
        name: name.trim(),
        code: code.trim() || null,
        description: description.trim() || null,
        gradingEnabled,
        centroCusto: centroCusto.trim() || null,
        status
      });
      setDetail(r.disciplina);
      await load();
    } catch (e) {
      void openAlert({ title: "Erro", message: friendlyError(e) });
    } finally {
      setBusy(false);
    }
  }

  async function addOffering() {
    if (!turmaPick) return;
    try {
      const r = await api.addOffering(disciplina.id, turmaPick);
      setDetail(r.disciplina);
      setTurmaPick("");
      await load();
    } catch (e) {
      void openAlert({ title: "Erro", message: friendlyError(e) });
    }
  }
  async function rmOffering(offeringId: string) {
    if (!confirm("Remover associação com esta turma?")) return;
    try {
      const r = await api.removeOffering(disciplina.id, offeringId);
      setDetail(r.disciplina);
      await load();
    } catch (e) {
      void openAlert({ title: "Erro", message: friendlyError(e) });
    }
  }

  const availableTurmas = turmas.value.filter(
    (t) => !detail.offerings.some((o) => o.turmaId === t.id) && !t.archivedAt
  );

  return (
    <Modal title={`Editar: ${detail.name}`} onClose={() => (editing.value = null)} wide>
      <Field label="Nome">
        <input
          type="text"
          value={name}
          onInput={(e) => setName((e.target as HTMLInputElement).value)}
          aria-label="Nome"
          class="w-full rounded-md border border-border bg-background px-3 py-1.5 text-sm outline-none focus:border-primary"
        />
      </Field>
      <div class="grid grid-cols-2 gap-2">
        <Field label="Código">
          <input
            type="text"
            value={code}
            onInput={(e) => setCode((e.target as HTMLInputElement).value)}
            aria-label="Código"
            class="w-full rounded-md border border-border bg-background px-3 py-1.5 font-mono text-sm outline-none focus:border-primary"
          />
        </Field>
        <Field label="Status">
          <select
            value={status}
            onChange={(e) => setStatus((e.target as HTMLSelectElement).value as any)}
            aria-label="Status"
            class="w-full rounded-md border border-border bg-background px-2 py-1.5 text-sm outline-none focus:border-primary"
          >
            <option value="draft">Rascunho</option>
            <option value="active">Ativa</option>
            <option value="closed">Encerrada</option>
          </select>
        </Field>
      </div>
      <Field label="Descrição">
        <textarea
          value={description}
          onInput={(e) => setDescription((e.target as HTMLTextAreaElement).value)}
          rows={2}
          aria-label="Descrição"
          class="w-full resize-y rounded-md border border-border bg-background px-3 py-1.5 text-sm outline-none focus:border-primary"
        />
      </Field>
      <Field label="Centro de custo">
        <input
          type="text"
          value={centroCusto}
          onInput={(e) => setCentroCusto((e.target as HTMLInputElement).value)}
          aria-label="Centro de custo"
          placeholder="Opcional (ex: Idiomas, Reforço escolar)"
          class="w-full rounded-md border border-border bg-background px-3 py-1.5 text-sm outline-none focus:border-primary"
        />
      </Field>
      <label class="flex items-center gap-2 text-xs">
        <Switch
          checked={gradingEnabled}
          onCheckedChange={(v) => setGrading(v)}
        />
        Tem nota
      </label>

      <div class="rounded-md border border-border bg-background/50 p-3">
        <h3 class="mb-2 text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
          Turmas associadas (offerings)
        </h3>
        {detail.offerings.length === 0 ? (
          <p class="mb-2 text-[11px] italic text-muted-foreground">Nenhuma turma associada.</p>
        ) : (
          <ul class="mb-2 space-y-1">
            {detail.offerings.map((o) => (
              <li
                key={o.id}
                class="flex items-center gap-2 rounded-md border border-border bg-card px-2 py-1.5 text-xs"
              >
                <span class="flex-1 truncate font-semibold">
                  {o.turmaName} {o.turmaCode && <span class="font-mono text-muted-foreground">({o.turmaCode})</span>}
                </span>
                <button
                  type="button"
                  onClick={() => rmOffering(o.id)}
                  class="rounded p-1 text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
                  title="Remover"
                  aria-label="Remover"
                >
                  <X width="0.9em" height="0.9em" />
                </button>
              </li>
            ))}
          </ul>
        )}
        {availableTurmas.length > 0 && (
          <div class="flex gap-1.5">
            <select
              value={turmaPick}
              onChange={(e) => setTurmaPick((e.target as HTMLSelectElement).value)}
              aria-label="Adicionar turma"
              class="flex-1 rounded-md border border-border bg-background px-2 py-1 text-xs"
            >
              <option value="">— Adicionar turma —</option>
              {availableTurmas.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name} {t.code ? `(${t.code})` : ""}
                </option>
              ))}
            </select>
            <button
              type="button"
              onClick={addOffering}
              disabled={!turmaPick}
              class="rounded-md border border-border bg-background px-2 py-1 text-xs font-semibold hover:bg-accent disabled:opacity-50"
            >
              + Add
            </button>
          </div>
        )}
      </div>

      <div class="rounded-md border border-border bg-background/50 p-3">
        <h3 class="mb-2 text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
          Professores ({detail.teachers.length})
        </h3>
        {detail.teachers.length === 0 ? (
          <p class="text-[11px] italic text-muted-foreground">
            Use a tela Professores pra adicionar e depois associe aqui. (UI completa em
            evolução.)
          </p>
        ) : (
          <ul class="space-y-1">
            {detail.teachers.map((p) => (
              <li
                key={p.userId}
                class="flex items-center gap-2 rounded-md border border-border bg-card px-2 py-1.5 text-xs"
              >
                <span class="flex-1 truncate font-semibold">{p.name}</span>
                <span class="truncate font-mono text-[10px] text-muted-foreground">{p.email}</span>
              </li>
            ))}
          </ul>
        )}
      </div>

      <ModalFooter>
        <button
          type="button"
          onClick={() => (editing.value = null)}
          disabled={busy}
          class="rounded-md border border-border bg-background px-3 py-1.5 text-xs font-semibold hover:bg-accent disabled:opacity-50"
        >
          Fechar
        </button>
        <button
          type="button"
          onClick={save}
          disabled={busy}
          class="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-xs font-bold text-white hover:brightness-110 disabled:opacity-50"
        >
          {busy ? <Loader2 className="size-3.5 animate-spin" /> : <Save width="1em" height="1em" />}
          Salvar
        </button>
      </ModalFooter>
    </Modal>
  );
}

function Modal({
  title,
  onClose,
  wide,
  children
}: {
  title: string;
  onClose: () => void;
  wide?: boolean;
  children: any;
}) {
  return (
    <div
      class="fixed inset-0 z-50 grid place-items-center bg-background/80 p-3 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        class={`flex max-h-[92vh] w-full ${wide ? "max-w-2xl" : "max-w-lg"} flex-col overflow-hidden rounded-md border border-border bg-card shadow-2xl`}
        onClick={(e) => e.stopPropagation()}
      >
        <header class="flex items-center justify-between border-b border-border px-4 py-3">
          <h2 class="flex items-center gap-2 text-base font-bold">
            <BookOpen width="1.2em" height="1.2em" className="text-primary" />
            {title}
          </h2>
          <button
            type="button"
            onClick={onClose}
            title="Fechar"
            aria-label="Fechar"
            class="rounded-md border border-border bg-background p-1 hover:bg-accent"
          >
            <X width="1em" height="1em" />
          </button>
        </header>
        <div class="flex-1 space-y-3 overflow-y-auto p-4">{children}</div>
      </div>
    </div>
  );
}
function Field({ label, children }: { label: string; children: any }) {
  return (
    <label class="block">
      <span class="mb-1 block text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
        {label}
      </span>
      {children}
    </label>
  );
}
function ModalFooter({ children }: { children: any }) {
  return (
    <div class="sticky bottom-0 -mx-4 -mb-4 mt-2 flex items-center justify-end gap-2 border-t border-border bg-card px-4 py-3">
      {children}
    </div>
  );
}
